"use client"

import * as React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"

import { cn } from "@/lib/utils"

/**
 * The invalid treatment lives HERE rather than on the item, because
 * `aria-invalid` does too: `role="radiogroup"` supports it and `role="radio"`
 * does not (CRCB2B-634, spec "Invalid state is announced on the group"). The
 * group therefore reaches down to tint its items' resting boundaries, giving the
 * ticket's "visually highlight affected controls" a real mechanism instead of a
 * rule on the item that only a non-conforming `aria-invalid` on the item itself
 * would ever trigger.
 *
 * The two `:not()`s are not decoration — they make this rule non-overlapping
 * with the item's checked and disabled rules, so which one wins is settled by
 * the selectors rather than by Tailwind's class ordering. Checked items keep
 * `--primary` (the *group* failed validation, not the option the user picked —
 * the same reasoning that puts Choice List's invalid treatment on its container)
 * and disabled items keep `--border-disabled`.
 *
 * DERIVED, not designed: Figma specifies no invalid state for any selection
 * control. The 1px `--border-error` edge is taken from Text Input, and the
 * message renders below the group via `FieldError`.
 */
const radioGroupInvalidItems =
  "[&[aria-invalid='true']_[data-slot=radio-group-item]:not([data-state=checked]):not([data-disabled])]:border-border-error"

function RadioGroup({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn("grid gap-3", radioGroupInvalidItems, className)}
      {...props}
    />
  )
}

/**
 * SRAM radio (CRCB2B-634). Restyled from Figma `Controls` → Radio (`75:183`).
 *
 * Geometry: a 24px circle (`size-6`), up from shadcn's 16px.
 *
 * The checked treatment is NOT shadcn's small dot inside a thin ring — Figma
 * (`75:188`) draws an 8px-thick `--primary` ring over a white fill, which reads
 * as a solid dark disc with a white centre dot. That is expressed here as a
 * border-width change on the item itself, so the lucide `CircleIcon` indicator
 * is gone; `RadioGroupPrimitive.Indicator` is kept as an empty marker so call
 * sites can still target `[data-slot=radio-group-indicator]`.
 *
 * Colors:
 *   - unchecked       white fill + `--border-strong` #676767
 *   - unchecked hover `--accent-hover` #d6d6d6 fill + `--border-strong-hover`
 *   - checked         8px `--primary` ring + white centre
 *   - checked hover   8px `--primary-hover` #4a4a4a ring + `--accent-hover`
 *                     centre (`75:195`)
 *   - disabled        unchecked → `--border-disabled` boundary; checked → the
 *                     8px ring recolors to the shared `--disabled` #bcbcbc
 *
 * DISCREPANCY, built to the spec rather than the node: Figma's hover-unchecked
 * variant (`75:192`) declares `--border-width-md` **2px**, the only control node
 * in the section that does. The checkbox's own hover (`22:51`) and every other
 * radio state stay at 1px, and the approved spec states 1px in every state, so
 * this is built at 1px and raised with design as a likely Figma slip.
 *
 * Focus keeps the shared primitive treatment (border swap + 3px ring).
 */
function RadioGroupItem({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        "peer aspect-square size-6 shrink-0 rounded-full border border-border-strong bg-background shadow-ui transition-colors outline-none",
        "hover:data-[state=unchecked]:border-border-strong-hover hover:data-[state=unchecked]:bg-accent-hover",
        // The checked "dot" is an 8px border over the fill, per Figma.
        //
        // The 8px WIDTH is unscoped — it is the checked indicator itself and must
        // survive focus, or the control stops looking selected. Only the COLOR is
        // scoped `not-focus-visible`, so a focused checked radio keeps its 8px ring
        // and recolors it to `--ring`, which is exactly Figma's focus rule applied to
        // the element that happens to BE the border here.
        //
        // Without the scope this tied with `focus-visible:border-ring` at (0,2,0) and
        // won on ordering, so a focused checked radio showed no border change at all
        // — leaving only the ~2.46:1 ring as its focus indicator. See the fuller note
        // in `checkbox.tsx`.
        "data-[state=checked]:border-8 not-focus-visible:data-[state=checked]:border-primary",
        "hover:data-[state=checked]:border-primary-hover hover:data-[state=checked]:bg-accent-hover",
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
        // No `aria-invalid:` rule here on purpose — the invalid treatment is
        // driven from `RadioGroup`, which is the node that carries the attribute.
        // See the comment on `radioGroupInvalidItems` above.
        "disabled:pointer-events-none disabled:cursor-not-allowed",
        "data-[disabled]:data-[state=unchecked]:border-border-disabled",
        "data-[disabled]:data-[state=checked]:border-disabled",
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator data-slot="radio-group-indicator" />
    </RadioGroupPrimitive.Item>
  )
}

export { RadioGroup, RadioGroupItem }
